import { create } from 'zustand'

/**
 * De kant van de kiosk en de beheerpagina naar `worker/beheer.ts`
 * (docs/beheer.md). De vormen hieronder zijn dezelfde als daar; een eigen
 * kopie, want de Worker en de app hebben elk hun eigen typen (DOM tegenover
 * Workers).
 */

export interface Dag {
  kliks: number
  sessies: number
  duur: number
  doelen: Record<string, number>
  uren: number[]
}

export interface Totaal {
  apparaat: string
  kiosk: string
  bijgewerkt: number
  dagen: Record<string, Dag>
}

export type Inlog =
  | { ok: true; token: string; verloopt: number }
  | { ok: false; reden: 'onjuist'; over: number }
  | { ok: false; reden: 'geblokkeerd'; wacht: number }
  | { ok: false; reden: 'verbinding' | 'fout' }

export async function logIn(wachtwoord: string): Promise<Inlog> {
  let antwoord: Response
  try {
    antwoord = await fetch('/api/beheer/inloggen', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ wachtwoord }),
    })
  } catch {
    return { ok: false, reden: 'verbinding' }
  }
  const data = await antwoord.json().catch(() => ({})) as Record<string, unknown>
  if (antwoord.ok && typeof data.token === 'string') return { ok: true, token: data.token, verloopt: Number(data.verloopt) }
  if (antwoord.status === 401) return { ok: false, reden: 'onjuist', over: Number(data.over ?? 0) }
  if (antwoord.status === 429) return { ok: false, reden: 'geblokkeerd', wacht: Number(data.wacht ?? 600) }
  return { ok: false, reden: 'fout' }
}

export async function logUit(token: string) {
  try {
    await fetch('/api/beheer/uitloggen', { method: 'POST', headers: { authorization: `Bearer ${token}` } })
  } catch {
    // Zonder verbinding vervalt hij vanzelf.
  }
}

export type Monitoring = {
  totalen: Totaal[]
  proef: boolean
  /** Het schijfpakket: alleen de cijfers van deze kiosk, met het adres waar alle kiosks samen staan. */
  lokaal?: { publiek: string }
}

/** null: de sessie is verlopen of ingetrokken. */
export async function haalMonitoring(token: string): Promise<Monitoring | null> {
  const antwoord = await fetch('/api/beheer/monitoring', { headers: { authorization: `Bearer ${token}` }, cache: 'no-store' })
  if (antwoord.status === 401) return null
  if (!antwoord.ok) throw new Error(`monitoring ${antwoord.status}`)
  return antwoord.json()
}

/**
 * De ingelogde sessie, alleen in het geheugen: herladen is opnieuw inloggen.
 * In de kiosk wist het sluiten van het paneel hem ook (`Instellingen.tsx`),
 * zodat een bezoeker na de standbeheerder niet zo het beheer in kan.
 */
interface BeheerState {
  token: string | null
  zet: (token: string | null) => void
}

export const useBeheer = create<BeheerState>((set) => ({
  token: null,
  zet: (token) => set({ token }),
}))
